<template>
  <div>
    <v-data-table
      id="offers-table-list"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'fas fa-angle-double-left',
        lastIcon: 'fas fa-angle-double-right',
        prevIcon: 'fas fa-angle-left',
        nextIcon: 'fas fa-angle-right',
        'items-per-page-text': $t('offer-manager.offers-list.footer-num-x-page'),
        'items-per-page-options': [10, 25, 50],
      }"
      :headers="headers"
      :item-class="getRowClass"
      :items="offerList"
      :items-per-page="tablePagination.itemsPerPage"
      :loading="loading || offerListLoading"
      :loading-text="$t('honda.offers.list.loading')"
      :no-data-text="
        $t(
          offerList && offerList.length === 0 && text
            ? 'offer-manager.offers-list.no-data-search'
            : 'offer-manager.offers-list.no-data',
          {search: text},
        )
      "
      :no-results-text="$t('offer-manager.offers-list.no-data-search')"
      :page="tablePagination.page"
      :server-items-length="total"
      :sort-by="sortBy.column"
      :sort-desc="sortBy.sort === 'desc'"
      class="elevation-0 pt-4"
      @click:row="viewOfferSummary"
      @update:items-per-page="onItemsPerPageChange"
      @update:options="onSortByChange"
      @update:page="onPageChange">
      <template #footer.page-text="{pageStart, pageStop, itemsLength}">
        {{ pageStart }}-{{ pageStop }} {{ $t('honda.registration.list.of') }} {{ itemsLength }}
      </template>

      <template v-slot:item.createDate="{item}">
        <span class="text-item">{{ item.createDate | formatDate }}</span>
      </template>

      <template v-slot:item.client="{item}">
        <m-copy
          v-if="item.type !== OfferType.subagent"
          :text-to-copy="`${getFullName(item)}, ${item.client.email}, ${item.client.phone}`"
          :show-client-history="true"
          @onCopy="onCopyClick"
          @onHistoryClick="handleHistoryClick(item.client.id)">
          <label-tooltip :is-tooltip-enabled="false">
            <div class="customer text-left py-1">
              <span class="customer-header">{{ getFullName(item) }}</span
              ><br />

              <span v-if="item.client.email" class="customer-email">{{ item.client.email }}<br /></span>

              <m-phone-label v-if="item.client.phone" :value="item.client.phone" class="customer-phone" />
            </div>
          </label-tooltip>
        </m-copy>

        <div v-else class="customer py-3 px-2 px-md-0">
          <p class="customer-header">{{ $t('offer-manager.offers-list.lbl-subagent') }}</p>
        </div>
      </template>

      <template v-slot:item.vehicle.model="{item}">
        <v-tooltip color="#222" bottom>
          <template v-slot:activator="{on, attrs}">
            <div class="customer py-3 px-2 px-md-0" v-bind="attrs" v-on="on">
              <p class="customer-header">{{ getModel(item.vehicle.model.name) }}</p>
            </div>
          </template>

          <div>
            <p class="mb-0">{{ item.vehicle.model.name }}</p>
            <p class="mb-0" v-if="item.vehicle.model.subname">{{ item.vehicle.model.subname }}</p>
          </div>
        </v-tooltip>
      </template>

      <template v-slot:item.offerCode="{item}">
        <m-copy
          v-if="item.status !== Status.Pending && item.status !== Status.Refused"
          :text-to-copy="item.offerCode || ''"
          align="center"
          @onCopy="onCopyClick">
          <div class="text-item">{{ item.offerCode }}</div>
        </m-copy>
      </template>

      <template v-slot:item.attendedBy="{item}">
        <span class="text-item">{{ item.attendedBy }}</span>
      </template>

      <template v-slot:item.status="{item}">
        <m-chip
          v-for="(chip, index) in [statusChip(item)]"
          :key="index"
          :icon="chip.icon"
          :text="chip.text"
          :tooltip="chip.tooltip"
          :trailing-icon="chip.trailingIcon"
          :variant="chip.variant" />
      </template>

      <template v-slot:item.followUp="{item}">
        <v-tooltip v-if="item.expired" :max-width="300" bottom color="black">
          <template v-slot:activator="{on}">
            <p v-on="on" class="text-expired">
              {{ $t('offers.filters.expired') }}
            </p>
          </template>

          <span>{{ $t('offers.tooltip.expired') }}</span>
        </v-tooltip>

        <v-tooltip v-else-if="item.followUp" :max-width="300" bottom color="black">
          <template v-slot:activator="{on}">
            <span :class="`${isPastToday(item.followUp.dueDate) ? 'text-expired' : ''}`" v-on="on">
              {{ item.followUp.dueDate | formatDate }}
            </span>
          </template>

          <span>{{ item.followUp.text }}</span>
        </v-tooltip>
      </template>

      <template v-slot:item.actions="{item}">
        <offer-table-actions :item="item" @changeStatus="onChangeStatus" />
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, nextTick, onMounted, PropType, ref, toRefs, watch} from '@vue/composition-api'

import MChip from '@/components/atom/MChip/index.vue'
import MCopy from '@/components/atom/MCopy.vue'
import LabelTooltip from '@/components/text/LabelTooltip.vue'
import {useOffersList} from '@/composables/offers-list'
import {useGtm} from '@/plugins/gtm'
import {Offer, OfferDoc, OfferType, Pagination, Status} from '@/types/offers'
import {InputTypes} from '@/utils/constants'
import {isPastToday} from '@/utils/date-helper'
import {mapGetters} from '@/utils/map-store'
import {offerStatusChip} from '@/utils/offer-status-chip'

import OfferTableActions from './OfferTableActions.vue'

export default defineComponent({
  name: 'OfferTable',
  components: {
    LabelTooltip,
    OfferTableActions,
    MChip,
    MCopy,
  },
  props: {
    statuses: {
      type: Array as PropType<Status[]>,
      required: true,
    },
    text: {
      type: String,
      default: '',
    },
    showExpired: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, {root, emit}) {
    const gtm = useGtm()

    const tablePagination = ref<Pagination>({page: 1, itemsPerPage: 10})

    const {getGroup: group, getSortBy: sortBy} = mapGetters(root.$store.getters)
    const {statuses: propStatuses, text: propText, showExpired: propShowExpired} = toRefs(props)

    const {
      data: offerList,
      debounceRefetch,
      isLoading: offerListLoading,
      pagination,
      refetch,
      total: totalOffers,
    } = useOffersList<OfferDoc>({
      dealerId: group.value.dealerId,
      dealerCode: group.value.code,
      pagination: {
        from: calculateFrom(tablePagination.value),
        size: tablePagination.value.itemsPerPage,
      },
      queryParams: {statuses: propStatuses.value},
      externalText: propText,
      externalStatuses: propStatuses,
      externalRange: propShowExpired,
    })

    const headers = ref([
      {
        text: root.$t('offer-manager.offers-list.lbl-creation'),
        align: 'center',
        value: 'createDate',
        class: 'text-header px-0',
        width: '10%',
        sortable: true,
      },
      {
        text: root.$t('offer-manager.offers-list.lbl-name-email'),
        align: 'left',
        value: 'client',
        class: 'text-header',
        width: '20%',
        sortable: true,
      },
      {
        text: root.$t('offer-manager.offers-list.lbl-model'),
        align: 'left',
        value: 'vehicle.model',
        class: 'text-header',
        width: '10%',
        sortable: true,
      },
      {
        text: root.$t('offer-manager.offers-list.lbl-num-offer'),
        align: 'center',
        value: 'offerCode',
        class: 'text-header',
        width: '10%',
        sortable: true,
      },
      {
        text: root.$t('offer-manager.offers-list.lbl-attended-by'),
        align: 'left',
        value: 'attendedBy',
        class: 'text-header',
        width: '20%',
        sortable: true,
      },
      {
        text: root.$t('offer-manager.offers-list.lbl-status'),
        align: 'left',
        value: 'status',
        class: 'text-header',
        width: '15%',
        sortable: true,
      },
      {
        text: root.$t('offer-manager.offers-list.lbl-follow-up'),
        align: 'center',
        value: 'followUp',
        class: 'text-header',
        width: '10%',
        sortable: true,
      },
      {
        align: 'center',
        value: 'actions',
        class: 'text-header',
        width: '5%',
        sortable: false,
      },
    ])

    const loading = ref(false)

    const total = computed(() => totalOffers.value || 0)

    onMounted(() => {
      nextTick(() => {
        window.document.querySelector('.dashboard-body')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      })
    })

    function getModel(model = ''): string {
      return model.split(' ')[0]
    }

    function onCopyClick() {
      root.$store.dispatch('showAlertSuccess', 'common.copy-success')
    }

    function handleHistoryClick(id: string) {
      root.$router.push({name: 'ClientHistory', params: {id}})
    }

    function viewOfferSummary(offer: Offer) {
      if (!offer?.id) {
        console.error('This offer does not exists', offer)
        return
      }

      gtm?.trackEvent({
        category: 'Offers',
        action: 'ViewOfferSummary',
        label: 'FromOffersList',
      })

      root.$router.push({name: 'ResumeOfferMotorbike', params: {id: offer.id}})
    }

    function getFullName(item: OfferDoc) {
      return `${item.client?.name || ''} ${item.client?.surname || ''}`
    }

    function onChangeStatus(offer: Offer, status: Status) {
      emit('changeStatus', offer, status)
    }

    function statusChip(offer: any) {
      return offerStatusChip(offer, (key: string, params?: Record<string, unknown>) =>
        root.$t(key, params || {}).toString(),
      )
    }

    function calculateFrom({page, itemsPerPage}: Pagination) {
      return !page ? 0 : (page - 1) * (itemsPerPage || 10)
    }

    function updatePagination({page, itemsPerPage}: Pagination) {
      if (page) {
        pagination.value.from = calculateFrom({page, itemsPerPage: pagination.value.size})
        tablePagination.value.page = page
      }

      if (itemsPerPage) {
        pagination.value.size = itemsPerPage
        tablePagination.value.itemsPerPage = itemsPerPage
      }
    }

    function onPageChange(page: any) {
      updatePagination({page})
    }

    function onItemsPerPageChange(itemsPerPage: any) {
      updatePagination({itemsPerPage})
    }

    function onSortByChange(options: any) {
      let [column] = options.sortBy

      if (column === 'client') {
        column = 'user'
      }

      if (column === 'vehicle.model') {
        column = 'model'
      }

      if (column === 'followUp') {
        options.sortDesc = [true]
      }

      const sort = options.sortDesc[0] === false ? 'asc' : 'desc'
      pagination.value.column = column
      pagination.value.sort = sort

      debounceRefetch()

      root.$store.dispatch('setSortBy', {column, sort})
    }

    function getRowClass(item: OfferDoc) {
      return `click row-${item.id}`
    }

    watch(
      [propText, propStatuses, propShowExpired],
      () => {
        updatePagination({page: 1})
      },
      {deep: true},
    )

    return {
      getFullName,
      getModel,
      getRowClass,
      headers,
      InputTypes,
      isPastToday,
      loading,
      offerList,
      offerListLoading,
      OfferType,
      onCopyClick,
      handleHistoryClick,
      onItemsPerPageChange,
      onPageChange,
      onSortByChange,
      onChangeStatus,
      sortBy,
      Status,
      statusChip,
      tablePagination,
      total,
      viewOfferSummary,
      refetch,
    }
  },
})
</script>

<style scoped>
.text-header {
  letter-spacing: 0;
  color: var(--v-gray-lighten5);
  text-align: left;
  font: normal normal 600 13px/15px 'ProximaNovaExCnSBold';
  text-transform: uppercase;
  opacity: 1;
}

.text-item {
  letter-spacing: 0;
  text-align: left;
  font: normal normal normal 15px/22px 'ProximaNovaRegular';
  color: #000;
  opacity: 1;
}

.text-expired {
  font-size: 14px;
  line-height: 1.4;
  color: #dc182d !important;
  margin: 0;
}

.icon-mapit {
  text-align: center;
  letter-spacing: 0;
  color: var(--v-gray-base);
  opacity: 1;
}

.customer-header {
  font: normal normal normal 15px/22px 'ProximaNovaSemibold' !important;
  color: var(--v-gray-darken4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 !important;
  margin: 0 !important;
  line-height: 26px;
}

.customer-phone,
.customer-email {
  font-family: 'ProximaNovaRegular', sans-serif;
  color: var(--v-gray-lighten5);
  font-size: 14px !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 !important;
  margin: 0 !important;
  line-height: 20px;
}
</style>
